import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface OnboardingCheckProps {
  children: React.ReactNode;
}

// Single source of truth for onboarding routing: profiles.onboarding_completed.
// Same flag OnboardingWizard reads on its own pre-mount check, so the two
// can't disagree and ping-pong (which produced React error #185).
//
// Belt-and-suspenders against any future divergence:
//   • sessionStorage('onboarding-just-completed') is set by handleFinish
//     immediately before navigating to /dashboard. We trust it for one
//     mount — short-circuits the DB read for the just-completed case.
//   • 500ms grace before evaluating an unauthenticated/unloaded state, so
//     any in-flight writes have time to commit before we redirect.
//   • Circuit breaker: refuses to redirect more than MAX_REDIRECTS times
//     in a single session. If somehow we still ping-pong, the user gets
//     the dashboard instead of a React error.

const JUST_COMPLETED_KEY = 'onboarding-just-completed';
const REDIRECT_COUNTER_KEY = 'onboarding-check-redirects';
const MAX_REDIRECTS = 2;
const GRACE_MS = 500;

function consumeJustCompletedFlag(): boolean {
  try {
    const v = sessionStorage.getItem(JUST_COMPLETED_KEY);
    if (v === '1') {
      sessionStorage.removeItem(JUST_COMPLETED_KEY);
      return true;
    }
  } catch {}
  return false;
}

function bumpRedirectCounter(): number {
  try {
    const current = parseInt(sessionStorage.getItem(REDIRECT_COUNTER_KEY) ?? '0', 10) || 0;
    const next = current + 1;
    sessionStorage.setItem(REDIRECT_COUNTER_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export const OnboardingCheck: React.FC<OnboardingCheckProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [onboarded, setOnboarded] = useState(false);

  useEffect(() => {
    if (location.pathname === '/onboarding') {
      setChecking(false);
      return;
    }
    if (authLoading) return;
    if (!user?.id) {
      setChecking(false);
      return;
    }

    // Fast path: handleFinish just navigated us here and dropped a flag.
    // Trust it, render the dashboard, and don't touch the DB.
    if (consumeJustCompletedFlag()) {
      setOnboarded(true);
      setChecking(false);
      return;
    }

    let cancelled = false;
    const graceTimer = setTimeout(() => {
      if (cancelled) return;

      (async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled) return;
        if (error) {
          console.warn('[OnboardingCheck] profile read failed:', error.message);
        }

        if ((data as any)?.onboarding_completed === true) {
          setOnboarded(true);
          setChecking(false);
          // Reset the counter — we got to a stable state.
          try {
            sessionStorage.removeItem(REDIRECT_COUNTER_KEY);
          } catch {}
          return;
        }

        // No profile row? Self-heal from the session, then redirect.
        if (!data) {
          const meta = (user.user_metadata as any) ?? {};
          const { error: upsertErr } = await supabase
            .from('profiles')
            .upsert(
              {
                id: user.id,
                email: user.email ?? null,
                full_name: meta.full_name ?? meta.name ?? null,
                avatar_url: meta.avatar_url ?? meta.picture ?? null,
                onboarding_completed: false,
              },
              { onConflict: 'id' },
            );
          if (upsertErr) {
            console.warn('[OnboardingCheck] profile backfill failed:', upsertErr.message);
          }
        }

        const redirectCount = bumpRedirectCounter();
        if (redirectCount > MAX_REDIRECTS) {
          // Circuit breaker: we've redirected too many times this session.
          // Something is wrong with the flag; render the dashboard anyway.
          console.warn(
            `[OnboardingCheck] circuit breaker tripped after ${redirectCount} redirects — rendering dashboard.`,
          );
          setOnboarded(true);
          setChecking(false);
          return;
        }

        setChecking(false);
        navigate('/onboarding', { replace: true });
      })();
    }, GRACE_MS);

    return () => {
      cancelled = true;
      clearTimeout(graceTimer);
    };
  }, [user?.id, authLoading, location.pathname, navigate]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-foreground/30 border-t-foreground rounded-full mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  if (user && !onboarded && location.pathname !== '/onboarding') {
    return null;
  }

  return <>{children}</>;
};

export default OnboardingCheck;
